<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'fixedAsset',
    path: '/fixed-assets/:id',
    component: 'FixedAsset',
    params: [
      {
        name: 'id',
        prop: 'fixedAssetId',
      },
    ],
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FixedAsset',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    fixedAssetId: {
      type: String,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const item = ref(null);
  const formId = ref(null);
  const messages = ref({
    delete: $fn.value.pgettext('FixedAsset|Header', 'Delete fixed asset'),
  });
  const ux = ref({
    initialLoadSpinner: true,
  });
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_8564662037462133_parentTo = computed(() => ({
    name: 'app.fixedAssets',
  }));

  const $data = computed(() => ({
    item: item.value,
    formId: formId.value,
    messages: messages.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_8564662037462133_parentTo: b_8564662037462133_parentTo.value,
  }));

  const $$load_item = async () => {
    return $fn.value.fetch('read_fixed_asset', {
      asResource: true,
      parameters: { id: props.fixedAssetId },
    });
  };
  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'formId' },
      { name: 'messages' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_8564662037462133_parentTo', nodeId: '8564662037462133' },
    ],
    loaders: { item: $$load_item, formId: $$load_formId },
    ux: { initialLoadSpinner: true },
    dataRefs: {
      item,
      formId,
      messages,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_8564662037462133_parentTo,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [item.value.snapshot('name'), $fn.value.gettext('Fixed Asset')],
        icon: $config.value.fixedAsset.icon,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.fixedAssetId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('fixedAssetId');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_nilfv62bh_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_nilfv62bh =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_nilfv62bh_getActionArgs,
    });
  const inst_evh_7315092382398562_close_to16uhqcf =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_nilfv62bh,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_to16uhqcf],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_gqqgq9dua =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_nqyypi6j3 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_gqqgq9dua,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_nqyypi6j3],
    event: 'reload',
  });
  const act_4615685607825208_reloadComponentData_02ayh2guv =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_4615685607825208_clickPreventStop_xrs2z6ylq =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_reloadComponentData_02ayh2guv,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_4615685607825208_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4615685607825208_clickPreventStop_xrs2z6ylq],
      event: 'click.prevent.stop',
    });
  const act_2095089031469157_closeComponent_esfp44sre =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2095089031469157_success_cmro2wg2u =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_closeComponent_esfp44sre,
      event: 'success',
      displayName: 'closeComponent',
    });
  const inst_evh_2095089031469157_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2095089031469157_success_cmro2wg2u],
    event: 'success',
  });
  const act_2679161326024595_reloadComponentData_j7lsemoim =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2679161326024595_reload_pt4atqpcd =
    new actionTypes.EventHandlerImpl({
      action: act_2679161326024595_reloadComponentData_j7lsemoim,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_2679161326024595_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2679161326024595_reload_pt4atqpcd],
    event: 'reload',
  });
  const act_2789477278097085_reloadComponentData_tmqjfor2j =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2789477278097085_reload_m4zb3gw6f =
    new actionTypes.EventHandlerImpl({
      action: act_2789477278097085_reloadComponentData_tmqjfor2j,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_2789477278097085_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2789477278097085_reload_m4zb3gw6f],
    event: 'reload',
  });
  const act_8856043737157701_reloadComponentData_vxswhvqt8 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_8856043737157701_reload_sy7pxp44f =
    new actionTypes.EventHandlerImpl({
      action: act_8856043737157701_reloadComponentData_vxswhvqt8,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_8856043737157701_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8856043737157701_reload_sy7pxp44f],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_close_to16uhqcf,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_nqyypi6j3,
    inst_evh_7315092382398562_reload,
    inst_evh_4615685607825208_clickPreventStop_xrs2z6ylq,
    reload: inst_evh_4615685607825208_clickPreventStop_xrs2z6ylq,
    inst_evh_4615685607825208_clickPreventStop,
    inst_evh_2095089031469157_success_cmro2wg2u,
    inst_evh_2095089031469157_success,
    inst_evh_2679161326024595_reload_pt4atqpcd,
    inst_evh_2679161326024595_reload,
    inst_evh_2789477278097085_reload_m4zb3gw6f,
    inst_evh_2789477278097085_reload,
    inst_evh_8856043737157701_reload_sy7pxp44f,
    inst_evh_8856043737157701_reload,
  };

  const evh_7315092382398562_close_to16uhqcf = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_to16uhqcf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_nqyypi6j3 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_nqyypi6j3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_clickPreventStop_xrs2z6ylq = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4615685607825208_clickPreventStop_xrs2z6ylq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_success_cmro2wg2u = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_success_cmro2wg2u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_success = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2679161326024595_reload_pt4atqpcd = async (event, scope) => {
    return await actions.inst_evh_2679161326024595_reload_pt4atqpcd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2679161326024595_reload = async (event, scope) => {
    return await actions.inst_evh_2679161326024595_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2789477278097085_reload_m4zb3gw6f = async (event, scope) => {
    return await actions.inst_evh_2789477278097085_reload_m4zb3gw6f.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2789477278097085_reload = async (event, scope) => {
    return await actions.inst_evh_2789477278097085_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8856043737157701_reload_sy7pxp44f = async (event, scope) => {
    return await actions.inst_evh_8856043737157701_reload_sy7pxp44f.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8856043737157701_reload = async (event, scope) => {
    return await actions.inst_evh_8856043737157701_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="FixedAsset"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="page--v2 d-flex flex-column" v-if="loaded">
      <MaterialTabs
        :title="item.snapshot('name')"
        :parentText="gettext('Fixed Assets')"
        :parentTo="b_8564662037462133_parentTo"
        class="h-100"
      >
        <template #actions>
          <ItpButton
            variant="light"
            icon="fa-sync-alt"
            :text="gettext('Reload')"
            size="sm"
            :spinning="actions.reload.isRunning"
            @click.prevent.stop="
              evh_4615685607825208_clickPreventStop($event, {})
            "
          >
          </ItpButton>
          <BtnDeleteResource
            :resource="item"
            :dialogHeader="messages.delete"
            @success="evh_2095089031469157_success($event, {})"
          >
          </BtnDeleteResource>
        </template>
        <BTab :title="gettext('General')" class="container float-start">
          <ItpRow>
            <ItpCol :w="6">
              <UcFixedAssetGeneral
                :fixedAsset="item"
                :reload="lastLoaded"
                class="pb-4"
                @reload="evh_2679161326024595_reload($event, {})"
              >
              </UcFixedAssetGeneral>
            </ItpCol>
            <ItpCol :w="6">
              <UcFixedAssetWasteBin
                :fixedAsset="item"
                :reload="lastLoaded"
                class="pb-4"
                v-if="item && item.asset_type === 'waste_bin'"
                @reload="evh_2789477278097085_reload($event, {})"
              >
              </UcFixedAssetWasteBin>
              <UcCommentsList :parent="item" :reload="lastLoaded" class="pb-4">
              </UcCommentsList>
              <UcAttachmentsList
                :parent="item"
                :reload="lastLoaded"
                class="pb-4"
              >
              </UcAttachmentsList>
              <UcFixedAssetWorkItemsPanel
                :oid="item.id"
                :reload="lastLoaded"
                class="pb-4"
                @reload="evh_8856043737157701_reload($event, {})"
              >
              </UcFixedAssetWorkItemsPanel>
            </ItpCol>
          </ItpRow>
        </BTab>
      </MaterialTabs>
    </div>
  </ItpPage>
</template>
